import { useRequest } from 'features/request/useRequest';
import { useResetStoreData } from 'features/request/useResetStoreData';
import { useMethodsConfirmationDialog } from 'hooks/useMethodsConfirmationDialog';

import { useCallback, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';

import type { RatioIdType } from '../../types';
import { removeCatalogRatioGql } from '../catalogRatiosFilterQueries';

export const useRemoveRatio = ({ onSuccessAction }: { onSuccessAction: () => void }) => {
    const dispatch = useDispatch();

    const [removingRatio, setRemovingRatio] = useState<Exclude<RatioIdType, undefined>>(null);

    const [resetRemoveData] = useResetStoreData({ requestId: 'removeCatalogRatio' });

    const tempSuccessHandler = useCallback(() => {
        onSuccessAction();
        resetRemoveData(undefined);
    }, [resetRemoveData, onSuccessAction]);

    const [, , , removeRatioRequest] = useRequest({
        requestId: 'removeCatalogRatio',
        query: removeCatalogRatioGql,
        withEffect: false,
        dependenciesUpdater: tempSuccessHandler,
    });

    const removeRatioHandler = useCallback(() => {
        dispatch(removeRatioRequest({ catalogRatioId: removingRatio }));
    }, [dispatch, removeRatioRequest, removingRatio]);

    const { isOpenConfirmationDialog, handleOpenConfirmationDialog, handleCloseConfirmationDialog, handleActionQuery } =
        useMethodsConfirmationDialog({
            actionFn: removeRatioHandler,
        });

    const handleOpenRemoveDialog = useCallback(
        (ratioId: Exclude<RatioIdType, undefined>) => () => {
            setRemovingRatio(ratioId);
            handleOpenConfirmationDialog();
        },
        [handleOpenConfirmationDialog],
    );

    return useMemo(
        () => ({
            handleDelete: handleActionQuery,
            handleOpenRemoveDialog,
            isOpenConfirmationDialog,
            handleCloseConfirmationDialog,
        }),
        [handleActionQuery, handleCloseConfirmationDialog, handleOpenRemoveDialog, isOpenConfirmationDialog],
    );
};
